import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// iter182 cleanup: trimmed lucide-react import to just the 2 still
// used directly in this file. KPI strip icons (DollarSign,
// TrendingUp, TrendingDown, Percent) moved with CustomerKPIStrip.jsx.
import { Plus } from "lucide-react";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import StickyTop from "@/components/common/StickyTop";
import { btn } from "@/components/common/btn";
import { getClients, getClient, createClient, updateClient, deleteClient, getSites, createSite, updateSite, deleteSite, getEquipment, getServiceHistory, getProposals, getFacilityTypes, createFacilityType, getClientStats, createProposal, updateClientRating, updateClientFacilityType, updateSiteFacilityType, getManufacturers, createManufacturer, getEquipmentTypes, createEquipmentType, getParts, createPart, createEquipment, updateEquipment, getEquipmentNames, createEquipmentName, deleteEquipment, archiveEquipment, restoreEquipment, getArchivedEquipment, getServiceTypes, createServiceType, archiveClient, restoreClient, getArchivedClients, getWorkOrders, getWorkOrder, updateWorkOrder, updateProposal, getMasterScopes, getMasterEquipment, getMasterParts, createMasterScope, createMasterEquipment, createMasterPart, getInspections, getSettings, lookupZip, lookupTaxRate, getClientDocuments, setClientCustomerNumber, getClientCommunications } from "@/lib/api";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
// Checkbox + Badge imports removed in iter181 — were orphaned by
// previous tab extractions; verified zero JSX uses remain.
// AlertDialog primitives removed in iter181 — moved with the
// Delete Site Confirmation to DeleteSiteConfirmDialog.jsx.
// Dialog primitives removed in iter181 — moved with the Permanent
// Delete Customer dialog to PermanentDeleteDialog.jsx. DialogTrigger
// was already orphaned by previous iters; bulk-removed here.
// DropdownMenu primitives removed in iter181 — already orphaned
// from a long-since-removed inline menu.
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import SubTabs from "@/components/common/SubTabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { getMountainToday } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import { SearchableSelect } from "@/components/ui/searchable-select";
// NoteLog import removed in iter181 — moved with the Notes block
// to ClientNotesBlock.jsx.
// WorkOrderTab default import removed in iter182 — moved with the
// Work Orders tab to ClientWorkOrdersTab.jsx.
import PartsCounter from "@/components/PartsCounter";
// InspectionReportInline import removed in iter180 — moved with the
// inspections tab to ClientInspectionsTab.jsx.
import DocumentPreviewModal from "@/components/DocumentPreviewModal";
// iter170 component split (step #2): pure helpers/constants moved
// into the dedicated `pages/clients/clientsUtils.js` module. This is
// the first step toward extracting full tab/panel sub-components
// (tracked in /app/memory/CODE_QUALITY_REFACTOR.md).
import { proposalStatusOptions } from "@/pages/clients/clientsUtils";
import { ClientHistoryTab } from "@/pages/clients/tabs/ClientHistoryTab";
import { ClientEquipmentTab } from "@/pages/clients/tabs/ClientEquipmentTab";
import { ClientDocumentsTab } from "@/pages/clients/tabs/ClientDocumentsTab";
import { ClientProposalsTab } from "@/pages/clients/tabs/ClientProposalsTab";
import { ClientInspectionsTab } from "@/pages/clients/tabs/ClientInspectionsTab";
import { ClientWorkOrdersTab } from "@/pages/clients/tabs/ClientWorkOrdersTab";
// iter181 component splits:
import { ArchivedCustomersSection } from "@/pages/clients/sections/ArchivedCustomersSection";
import { PermanentDeleteDialog } from "@/pages/clients/dialogs/PermanentDeleteDialog";
import { DeleteSiteConfirmDialog } from "@/pages/clients/dialogs/DeleteSiteConfirmDialog";
import { ClientGeneralTab } from "@/pages/clients/tabs/ClientGeneralTab";
import { ClientHeaderCard } from "@/pages/clients/sections/ClientHeaderCard";
import { guardEscapeKeyDown } from "@/lib/escapeGuard";

import { AddSiteDialog } from "@/pages/clients/dialogs/AddSiteDialog";
import { CustomerListTable } from "@/pages/clients/sections/CustomerListTable";
import { ArchiveCustomerSection } from "@/pages/clients/sections/ArchiveCustomerSection";
// Re-introduced Feb 2026 per user request — modal as a fast keyboard-
// driven alternative to the side-sheet for core identity edits.
import EditClientDialog from "@/pages/clients/dialogs/EditClientDialog";

// ============== CUSTOMER # FIELD (passcode-gated) ==============
// Extracted to `@/components/clients/CustomerNumberField` — re-imported above.

// Surface the server's reason (e.g. a permissions denial) instead of a blind "Failed".
const withDetail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" && d ? `${fallback}: ${d}` : fallback;
};


export default function Clients() {
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [archivedClients, setArchivedClients] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [groupBy, setGroupBy] = useState(""); // "" | facility | location | rating
  const [deleteConfirmClient, setDeleteConfirmClient] = useState(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [woSearchResults, setWoSearchResults] = useState([]);
  const [showWoDropdown, setShowWoDropdown] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [facilityTypeFilter, setFacilityTypeFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  // Iter170 cleanup: `isDialogOpen` + `editingClient` state removed
  // along with the AddEditClientDialog. The customer side-sheet is
  // now the only edit surface and uses `isSheetOpen` / `selectedClient`
  // (declared below) instead.
  // Feb 2026 — Re-added a lightweight "Quick Edit" dialog as an
  // alternative path (pencil icon on each row). Side-sheet remains
  // the primary deep-edit surface.
  const [editDialogClient, setEditDialogClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSites, setClientSites] = useState([]);
  const [clientEquipment, setClientEquipment] = useState([]);
  const [archivedEquipment, setArchivedEquipment] = useState([]);
  const [clientWorkOrders, setClientWorkOrders] = useState([]);
  const [clientServiceHistory, setClientServiceHistory] = useState([]);
  const [clientCommunications, setClientCommunications] = useState([]);
  const [clientDocuments, setClientDocuments] = useState([]);
  const [docSearch, setDocSearch] = useState("");
  const [docSort, setDocSort] = useState({ field: null, dir: null });
  const [docPreview, setDocPreview] = useState(null); // { title, src, download, email }
  const [clientProposals, setClientProposals] = useState([]);
  const [clientStats, setClientStats] = useState(null);
  const [clientInspections, setClientInspections] = useState([]);
  const [expandedInspId, setExpandedInspId] = useState(null);
  const [expandedInspData, setExpandedInspData] = useState(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [appSettings, setAppSettings] = useState(null);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [newFacilityType, setNewFacilityType] = useState("");
  const [isAddingFacilityType, setIsAddingFacilityType] = useState(false);
  const [isProposalDialogOpen, setIsProposalDialogOpen] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState("all");
  // Site edit-lock: once a site has address+city+state+zip all filled in, its
  // address fields auto-lock to read-only. User clicks the pencil "Edit"
  // button next to Add Site to unlock; "Done" re-locks. Resets to locked
  // whenever the user switches to another site.
  const [siteEditMode, setSiteEditMode] = useState(false);
  const [isSiteDialogOpen, setIsSiteDialogOpen] = useState(false);
  const [siteFormData, setSiteFormData] = useState({
    name: "",
    address: "",
    address_line_2: "",
    city: "",
    state: "",
    zip_code: "",
    facility_type: "",
    is_primary: false,
    tax_authority: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
  });
  const [siteContactMode, setSiteContactMode] = useState("existing"); // "existing" or "new"
  const [proposalFormData, setProposalFormData] = useState({
    name: "",
    site_id: "",
    value: 0,
    status: "draft",
  });
  
  // Equipment state
  const [isEquipmentDialogOpen, setIsEquipmentDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [parts, setParts] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [equipmentNames, setEquipmentNames] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [isAddingServiceType, setIsAddingServiceType] = useState(false);
  const [newServiceType, setNewServiceType] = useState("");
  const [isAddingManufacturer, setIsAddingManufacturer] = useState(false);
  const [isAddingType, setIsAddingType] = useState(false);
  const [isAddingPart, setIsAddingPart] = useState(false);
  const [isAddingEquipmentName, setIsAddingEquipmentName] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState("");
  const [newType, setNewType] = useState("");
  const [newPart, setNewPart] = useState("");
  const [newEquipmentName, setNewEquipmentName] = useState("");
  // Master library state
  const [masterScopes, setMasterScopes] = useState([]);
  const [masterEquipment, setMasterEquipment] = useState([]);
  const [masterParts, setMasterParts] = useState([]);
  const [librarySearch, setLibrarySearch] = useState("");
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [importedFromLibrary, setImportedFromLibrary] = useState(false);
  const [equipmentFormData, setEquipmentFormData] = useState({
    name: "",
    site_id: "",
    manufacturer_id: "",
    equipment_type_id: "",
    part_id: "",
    service_type: "",
    install_date: null,
    last_serviced: null,
    notes: [],
    notes_history: [],
    scope_name: "",
    manufacturer_name: "",
    part_name: "",
    part_number: "",
    description: "",
    part_cost: 0,
    material_margin: 50,
    freight: 0,
    labor_hours: 0,
    labor_cost: 40,
    labor_margin: 73.333,
    equipment_cost: 0,
    equipment_margin: 25,
    disposal_cost: 0,
    disposal_margin: 25,
    travel_cost: 0,
    travel_margin: 25,
    qty: 1,
    location: "",
    cost_code: "20-100000",
    department: "Service",
  });
  
  const [formData, setFormData] = useState({
    name: "",
    contacts: [],
    facility_type: "",
    rating: "",
    is_new_lead: false,
    payment_hold: false,
    address: "",
    address_line_2: "",
    city: "",
    state: "",
    zip_code: "",
    notes: [],
    notes_history: [],
    billing_contact_name: "",
    billing_contact_email: "",
    billing_contact_phone: "",
    customer_number: "",
    customer_number_requested_at: "",
    tax_exempt: false,
    hide_tax: false,
    tax_certificate_no: "",
  });
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", role: "", notes: "", is_primary: false });

  // Contact role options — seeded with 3 defaults, stored in localStorage
  const DEFAULT_CONTACT_ROLES = ["Building Manager", "Maintenance", "Project Manager"];
  const [contactRoles, setContactRoles] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("so_contact_roles") || "[]");
      const merged = [...new Set([...DEFAULT_CONTACT_ROLES, ...saved])].sort();
      return merged;
    } catch { return DEFAULT_CONTACT_ROLES; }
  });
  const [newRoleInput, setNewRoleInput] = useState("");
  const addContactRole = (name) => {
    const trimmed = name.trim();
    if (!trimmed || contactRoles.includes(trimmed)) return;
    const updated = [...contactRoles, trimmed].sort();
    setContactRoles(updated);
    const custom = updated.filter(r => !DEFAULT_CONTACT_ROLES.includes(r));
    localStorage.setItem("so_contact_roles", JSON.stringify(custom));
    return trimmed;
  };
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editingBillingContact, setEditingBillingContact] = useState(false);
  // Master lock toggle for the customer detail sheet's identity fields
  // (Site name + Address block + Customer # + Facility Type). Pencil at
  // the top-right of the sheet header flips it. Always re-locks when a
  // different customer is loaded.
  const [sheetLocked, setSheetLocked] = useState(true);
  const formLoadingRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | "saving" | "saved" | "error"
  // iter253 — refs kept in sync with the latest form + selected client so
  // the sheet close handler can flush a pending debounced save without
  // needing them in its dependency list (which would recreate the handler
  // on every keystroke).
  const formDataRef = useRef(null);
  const selectedClientRef = useRef(null);
  const savingInFlightRef = useRef(false);

  // Auto-save: debounce formData changes
  useEffect(() => {
    if (!selectedClient || !isSheetOpen) return;
    if (formLoadingRef.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveInline();
    }, 800);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
    // Debounced auto-save trigger — we want this to fire on formData
    // changes only. `autoSaveInline` is a stable closure that reads the
    // latest selectedClient/isSheetOpen at call time, so including them
    // in deps would cause the timer to reset unnecessarily on sheet open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const autoSaveInline = async () => {
    if (!selectedClient) return;
    savingInFlightRef.current = true;
    setSaveStatus("saving");
    // iter241 — notes shown in the sheet belong to a SITE when one is
    // selected/primary; otherwise they are CLIENT-level and live in
    // formData. Persist client-level edits (archive/restore) from
    // formData; site-level notes go out through the site branch below.
    const notesSite = selectedSiteId !== "all"
      ? clientSites.find(s => s.id === selectedSiteId)
      : (clientSites.length === 1 ? clientSites[0] : clientSites.find(s => s.is_primary));
    try {
      await updateClient(selectedClient.id, {
        name: formData.name,
        is_new_lead: formData.is_new_lead,
        payment_hold: formData.payment_hold,
        facility_type: selectedClient.facility_type,
        rating: selectedClient.rating || "",
        contacts: selectedClient.contacts || [],
        notes: notesSite ? (selectedClient.notes || []) : (formData.notes || []),
        notes_history: notesSite ? (selectedClient.notes_history || []) : (formData.notes_history || []),
        address: selectedClient.address || "",
        address_line_2: selectedClient.address_line_2 || "",
        city: selectedClient.city || "",
        state: selectedClient.state || "",
        zip_code: selectedClient.zip_code || "",
        billing_contact_name: formData.billing_contact_name || "",
        billing_contact_email: formData.billing_contact_email || "",
        billing_contact_phone: formData.billing_contact_phone || "",
        customer_number: formData.customer_number || "",
        customer_number_requested_at: formData.customer_number_requested_at || "",
        tax_exempt: formData.tax_exempt || false,
        hide_tax: !!formData.hide_tax,
        tax_certificate_no: formData.tax_certificate_no || "",
      });
      if (selectedSiteId !== "all") {
        const site = clientSites.find(s => s.id === selectedSiteId);
        if (site) {
          await updateSite(selectedSiteId, {
            ...site,
            address: formData.address,
            address_line_2: formData.address_line_2 || "",
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code,
            contacts: formData.contacts,
            notes: formData.notes, notes_history: formData.notes_history,
          });
          setClientSites(prev => prev.map(s => s.id === selectedSiteId ? {
            ...s,
            address: formData.address,
            address_line_2: formData.address_line_2 || "",
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code,
            contacts: formData.contacts,
            notes: formData.notes, notes_history: formData.notes_history,
          } : s));
        }
      } else if (primarySite) {
        await updateSite(primarySite.id, {
          ...primarySite,
          address: formData.address,
          address_line_2: formData.address_line_2 || "",
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          contacts: formData.contacts,
          notes: formData.notes, notes_history: formData.notes_history,
        });
        setClientSites(prev => prev.map(s => s.id === primarySite.id ? {
          ...s,
          address: formData.address,
          address_line_2: formData.address_line_2 || "",
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          contacts: formData.contacts,
          notes: formData.notes, notes_history: formData.notes_history,
        } : s));
      }
      setSelectedClient(prev => ({ ...prev, name: formData.name, is_new_lead: formData.is_new_lead, payment_hold: formData.payment_hold, billing_contact_name: formData.billing_contact_name, billing_contact_email: formData.billing_contact_email, billing_contact_phone: formData.billing_contact_phone, customer_number: formData.customer_number, tax_exempt: formData.tax_exempt, hide_tax: !!formData.hide_tax, tax_certificate_no: formData.tax_certificate_no || "" }));
      loadClients();
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      setSaveStatus("error");
      toast.error("Failed to auto-save — please try again");
    } finally {
      savingInFlightRef.current = false;
    }
  };

  // iter253 — Keep refs in sync so the sheet close handler + flushSaveClient
  // can access the latest state without re-registering.
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { selectedClientRef.current = selectedClient; }, [selectedClient]);

  // iter253 — Flush a pending debounced autosave synchronously. Called from
  // the sheet's onOpenChange when the user closes so trailing edits made
  // in the last 800ms window aren't silently discarded. Blocks the close
  // via `await` so the user waits until data is safely on the server.
  const flushSaveClient = async () => {
    // Only flush if a debounced save is armed (user typed within the
    // last 800ms). If nothing is pending, do nothing — avoids no-op
    // writes on plain close of a viewed-only customer.
    if (!autoSaveTimerRef.current) return;
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = null;
    if (!selectedClientRef.current || !formDataRef.current) return;
    if (savingInFlightRef.current) return;
    try {
      await autoSaveInline();
    } catch {
      /* toast already surfaced from autoSaveInline */
    }
  };

  const handleRatingChange = async (clientId, newRating) => {
    try {
      await updateClientRating(clientId, newRating);
      setClients(clients.map(c => 
        c.id === clientId ? { ...c, rating: newRating } : c
      ));
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient({ ...selectedClient, rating: newRating });
      }
      toast.success(`Rating updated to ${newRating}`);
    } catch (error) {
      toast.error("Failed to update rating");
    }
  };

  const handleFacilityTypeChange = async (newType) => {
    try {
      if (selectedSiteId !== "all" && selectedSite) {
        // Update site's facility type
        await updateSiteFacilityType(selectedSiteId, newType);
        setClientSites(clientSites.map(s =>
          s.id === selectedSiteId ? { ...s, facility_type: newType || null } : s
        ));
        toast.success("Site facility type updated");
      } else {
        // Update client's facility type
        await updateClientFacilityType(selectedClient.id, newType);
        setSelectedClient({ ...selectedClient, facility_type: newType || null });
        setClients(clients.map(c =>
          c.id === selectedClient.id ? { ...c, facility_type: newType || null } : c
        ));
        toast.success("Facility type updated");
      }
    } catch (error) {
      toast.error("Failed to update facility type");
    }
  };

  const handleAddFacilityTypeInline = async () => {
    if (newFacilityType.trim()) {
      try {
        await createFacilityType({ name: newFacilityType.trim() });
        await loadFacilityTypes();
        await handleFacilityTypeChange(newFacilityType.trim());
        setNewFacilityType("");
        setIsAddingFacilityType(false);
        toast.success("Facility type added");
      } catch (error) {
        toast.error("Facility type already exists");
      }
    }
  };

  const loadFacilityTypes = useCallback(async () => {
    try {
      const response = await getFacilityTypes();
      setFacilityTypes(response.data);
    } catch (error) {
      console.error("Failed to load facility types");
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadFacilityTypes();
    getSettings().then(res => setAppSettings(res.data)).catch(() => {});
    // `loadClients` reads the latest `search` from closure on each call;
    // explicit dep would force useCallback wrap with no behavior change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, loadFacilityTypes]);

  // Search work orders when typing
  useEffect(() => {
    if (!search || search.length < 2) {
      setWoSearchResults([]);
      setShowWoDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await getWorkOrders({ search });
        if (res.data && res.data.length > 0) {
          setWoSearchResults(res.data.slice(0, 8));
          setShowWoDropdown(true);
        } else {
          setWoSearchResults([]);
          setShowWoDropdown(false);
        }
      } catch {
        setWoSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Auto-open client from navigation state (e.g., after lead conversion or dispatch)
  useEffect(() => {
    const openClientId = location.state?.openClientId;
    const openTab = location.state?.openTab;
    const openWorkOrderId = location.state?.openWorkOrderId;
    const newWorkOrder = location.state?.newWorkOrder;
    const returnTo = location.state?.returnTo;
    const openPartsCounter = location.state?.openPartsCounter;
    const commandNewClient = location.state?.commandNewClient;
    // Cmd+K "New customer" Quick Action — fire the same instant-create
    // flow the page's own "+ Add Customer" button uses, then strip the
    // state so a back-nav doesn't re-create another row.
    if (commandNewClient) {
      handleInstantCreate();
      window.history.replaceState({}, document.title);
      return;
    }
    // UI sweep batch 5 — an existing work order always opens on its own full page.
    // Every producer still sends { openClientId, openTab: "workorders", openWorkOrderId }; we route it there.
    if (openWorkOrderId && !newWorkOrder) {
      const openWoTab = location.state?.openWoTab;
      navigate(`/work-orders/${openWorkOrderId}`, { replace: true, state: (returnTo || openWoTab) ? { returnTo, openWoTab } : undefined });
      return;
    }
    if (openPartsCounter && clients.length > 0) {
      const pc = clients.find(c => c.customer_type === "parts_counter");
      if (pc) {
        loadClientDetails(pc);
        window.history.replaceState({}, document.title);
        return;
      }
    }
    if (openClientId && clients.length > 0) {
      const client = clients.find(c => c.id === openClientId);
      const openSheet = (cl) => {
        // Clear the nav state up-front (values already captured above) so
        // an interim re-render can't re-trigger this async flow.
        window.history.replaceState({}, document.title);
        if (openWorkOrderId) {
          // iter219 fix — mirror navigateToWorkOrder(): AWAIT the full
          // client-sheet load (which settles selectedSiteId / clientSites
          // via setSelectedSiteId() AFTER its awaits) BEFORE mounting the
          // WorkOrderTab and handing it the pending WO id. Without the
          // await, the tab mounted early with selectedSiteId="all",
          // auto-opened the WO, then loadClientDetails's late
          // setSelectedSiteId() re-rendered the tab and dropped the user
          // back on the WO list (the customer) — never the specific WO.
          if (returnTo) setWoReturnTo(returnTo);
          // Tab is set synchronously too, so a slow/failed detail fetch can never leave the sheet on Equipment.
          const land = () => { setActiveDetailTab("workorders"); setPendingWorkOrderId(openWorkOrderId); };
          loadClientDetails(cl, "workorders").then(land).catch(land);
        } else if (newWorkOrder) {
          // Lead marked Won → land on Work Orders with a fresh WO form open.
          if (returnTo) setWoReturnTo(returnTo);
          const land = () => { setActiveDetailTab("workorders"); setTimeout(() => workOrderTabRef.current?.openNew(), 150); };
          loadClientDetails(cl, "workorders").then(land).catch(land);
        } else {
          loadClientDetails(cl, openTab || undefined);
        }
      };
      if (client) {
        openSheet(client);
      } else {
        // iter229 fix — the customers list is PAGINATED (iter201), so a
        // deep-link target (e.g. Proposal → Convert-to-WO / WO button)
        // may not be in the loaded page. Fetch it by id instead of
        // silently stranding the user on the customers list.
        getClient(openClientId)
          .then(r => { if (r.data) openSheet(r.data); })
          .catch(async () => {
            // iter236 — the stored client_id can be stale (deleted client).
            // If we know the target WO, resolve its CURRENT client instead
            // of silently stranding the user on the directory.
            if (openWorkOrderId) {
              try {
                const woRes = await getWorkOrder(openWorkOrderId);
                const realClientId = woRes.data?.client_id;
                if (realClientId && realClientId !== openClientId) {
                  const cRes = await getClient(realClientId);
                  if (cRes.data) { openSheet(cRes.data); return; }
                }
              } catch { /* fall through to toast */ }
            }
            toast.error("Customer not found — it may have been deleted");
            window.history.replaceState({}, document.title);
          });
      }
    } else if (!openClientId && openWorkOrderId && clients.length > 0) {
      // iter236 — some deep-links (legacy `openWOId` producers, or records
      // with a blank client_id) arrive with only a WO id. Resolve the WO's
      // current client instead of silently stranding on the directory.
      getWorkOrder(openWorkOrderId)
        .then(async (woRes) => {
          const cid = woRes.data?.client_id;
          if (!cid) throw new Error("no client");
          const client = clients.find(c => c.id === cid) || (await getClient(cid)).data;
          if (!client) throw new Error("no client");
          window.history.replaceState({}, document.title);
          const land = () => { setActiveDetailTab("workorders"); setPendingWorkOrderId(openWorkOrderId); };
          loadClientDetails(client, "workorders").then(land).catch(land);
        })
        .catch(() => {
          toast.error("Work order's customer not found — it may have been deleted");
          window.history.replaceState({}, document.title);
        });
    }
    // `loadClientDetails` is stable; only the navigation state should
    // re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, clients]);

  // Auto-lock the customer sheet whenever the route path changes — even
  // within the Customers tab, navigating away and coming back must not
  // leave the sheet in its prior unlocked state. Mirrors the desktop-app
  // expectation that "leave and return = read-only by default again."
  useEffect(() => {
    setSheetLocked(true);
    setEditingBillingContact(false);
    setEditingContactId(null);
    setIsAddingContact(false);
    setIsAddingNote(false);
    setSiteEditMode(false);
  }, [location.pathname]);


  // ─── Infinite-scroll pagination (MIRA ticket #266) ──────────────
  // The /api/clients endpoint already accepts skip+limit. We page in
  // 25 rows at a time and append; sort/filter/search controls run
  // client-side against whatever has been loaded so far. When the
  // server returns fewer rows than requested, we know we've hit the
  // end and stop the observer.
  const PAGE_SIZE = 25;
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadSentinelRef = useRef(null);
  const loadGenerationRef = useRef(0);   // race-cancel id for stale loads
  const skipRef = useRef(0);              // canonical paging cursor

  const loadClients = async (opts = {}) => {
    // Reset → fresh search/filter/full reload. Append → next page tap.
    const reset = !opts.append;
    if (reset) {
      setLoading(true);
      skipRef.current = 0;
      setHasMore(true);
    } else {
      if (loadingMore || !hasMore) return;
      setLoadingMore(true);
    }
    // Bump the generation so an in-flight response from a stale search
    // can't overwrite the new one (debounce-race protection).
    const gen = ++loadGenerationRef.current;
    try {
      const params = { skip: skipRef.current, limit: PAGE_SIZE };
      if (search) params.search = search;
      const activeReq = getClients(params);
      // Archived list is fetched only on the first page — keeping it
      // separate from the infinite scroll so the archive section under
      // the table remains accurate without paging.
      const archivedReq = reset ? getArchivedClients(search ? { search } : {}) : null;
      const [activeRes, archivedRes] = await Promise.all([activeReq, archivedReq]);
      if (gen !== loadGenerationRef.current) return;   // newer load won the race
      const rows = activeRes.data || [];
      const hdrTotal = parseInt(activeRes.headers?.["x-total-count"], 10);
      if (!Number.isNaN(hdrTotal)) setTotalCount(hdrTotal);
      setClients(prev => (reset ? rows : [...prev, ...rows]));
      skipRef.current += rows.length;
      setHasMore(rows.length === PAGE_SIZE);
      if (archivedRes) setArchivedClients(archivedRes.data || []);
    } catch (error) {
      if (gen !== loadGenerationRef.current) return;
      toast.error("Failed to load customers");
    } finally {
      if (gen === loadGenerationRef.current) {
        if (reset) setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  // IntersectionObserver — fires once when the sentinel scrolls into
  // view. We don't watch every scroll event (cheap on a 5k-row list).
  useEffect(() => {
    const node = loadSentinelRef.current;
    if (!node || !hasMore || loading) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        loadClients({ append: true });
      }
    }, { rootMargin: "200px" });
    io.observe(node);
    return () => io.disconnect();
    // `loadClients` reads search/skip from refs/closure — deps left
    // bare on purpose so we re-attach only when the list state matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, clients.length]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const uniqueLocations = useMemo(() => {
    const locs = new Set();
    clients.forEach(c => {
      const loc = [c.city, c.state].filter(Boolean).join(", ");
      if (loc) locs.add(loc);
    });
    return Array.from(locs).sort();
  }, [clients]);

  const sortedClients = useMemo(() => {
    // Parts Counter is ALWAYS the first row — immune to sorting and filters.
    const pcRow = clients.find(c => c.customer_type === "parts_counter");
    const pinned = pcRow ? [pcRow] : [];
    let filtered = clients.filter(c => c.customer_type !== "parts_counter");
    if (facilityTypeFilter) {
      filtered = filtered.filter(c => c.facility_type === facilityTypeFilter);
    }
    if (ratingFilter) {
      filtered = filtered.filter(c => c.rating === ratingFilter);
    }
    if (locationFilter) {
      filtered = filtered.filter(c => {
        const loc = [c.city, c.state].filter(Boolean).join(", ");
        return loc === locationFilter;
      });
    }
    if (!sortField) {
      const permanent = filtered.filter(c => c.is_permanent || c.is_sandbox);
      const rest = filtered.filter(c => !c.is_permanent && !c.is_sandbox);
      return [...pinned, ...permanent, ...rest];
    }
    const sorted = filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "name": aVal = (a.name || "").toLowerCase(); bVal = (b.name || "").toLowerCase(); break;
        case "facility_type": aVal = (a.facility_type || "").toLowerCase(); bVal = (b.facility_type || "").toLowerCase(); break;
        case "location": aVal = [a.city, a.state].filter(Boolean).join(", ").toLowerCase(); bVal = [b.city, b.state].filter(Boolean).join(", ").toLowerCase(); break;
        case "won_count": aVal = a.won_count || 0; bVal = b.won_count || 0; break;
        case "bid_count": aVal = a.bid_count || 0; bVal = b.bid_count || 0; break;
        case "total_bid": aVal = a.total_bid || 0; bVal = b.total_bid || 0; break;
        case "hit_rate": aVal = a.hit_rate ?? -1; bVal = b.hit_rate ?? -1; break;
        case "rating": { const r = (c) => (c.rating ? "ABCD".indexOf(c.rating) : 9); aVal = r(a); bVal = r(b); break; }
        case "total_revenue": aVal = a.total_revenue || 0; bVal = b.total_revenue || 0; break;
        case "rating": aVal = a.rating || "Z"; bVal = b.rating || "Z"; break;
        default: return 0;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    // Pin permanent clients (Sandbox Test Project) below Parts Counter
    const permanent = sorted.filter(c => c.is_permanent || c.is_sandbox);
    const rest = sorted.filter(c => !c.is_permanent && !c.is_sandbox);
    return [...pinned, ...permanent, ...rest];
  }, [clients, sortField, sortDirection, facilityTypeFilter, ratingFilter, locationFilter]);

  const handleArchiveEquipment = async (e, equipId) => {
    e.stopPropagation();
    try {
      await archiveEquipment(equipId);
      const archived = clientEquipment.find(eq => eq.id === equipId);
      setClientEquipment(clientEquipment.filter(eq => eq.id !== equipId));
      if (archived) setArchivedEquipment(prev => [...prev, { ...archived, archived: true, archived_at: new Date().toISOString() }]);
      toast.success("Equipment moved to history");
    } catch (error) {
      toast.error("Failed to archive equipment");
    }
  };

  const handleRestoreEquipment = async (e, equipId) => {
    e.stopPropagation();
    try {
      const res = await restoreEquipment(equipId);
      setArchivedEquipment(prev => prev.filter(eq => eq.id !== equipId));
      setClientEquipment(prev => [...prev, res.data]);
      toast.success("Equipment restored");
    } catch (error) {
      toast.error("Failed to restore equipment");
    }
  };

  // Get the primary site for this client
  const primarySite = useMemo(() => {
    return clientSites.find(s => s.is_primary) || null;
  }, [clientSites]);

  // When site changes, update formData to reflect that site's data
  const switchSiteData = (siteId) => {
    formLoadingRef.current = true;
    setSelectedSiteId(siteId);
    setSiteEditMode(false);
    if (siteId === "all") {
      const ps = clientSites.find(s => s.is_primary);
      if (ps) {
        setFormData(prev => ({
          ...prev,
          address: ps.address || "",
          city: ps.city || "",
          state: ps.state || "",
          zip_code: ps.zip_code || "",
          contacts: ps.contacts || [],
          notes: ps.notes || [], notes_history: ps.notes_history || [],
          billing_contact_name: selectedClient.billing_contact_name || "",
          billing_contact_email: selectedClient.billing_contact_email || "",
          billing_contact_phone: selectedClient.billing_contact_phone || "",
          customer_number: selectedClient.customer_number || "",
          customer_number_requested_at: selectedClient.customer_number_requested_at || "",
          tax_exempt: selectedClient.tax_exempt || false,
          hide_tax: !!selectedClient.hide_tax,
          tax_certificate_no: selectedClient.tax_certificate_no || "",
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          address: selectedClient.address || "",
          address_line_2: selectedClient.address_line_2 || "",
          city: selectedClient.city || "",
          state: selectedClient.state || "",
          zip_code: selectedClient.zip_code || "",
          contacts: selectedClient.contacts || [],
          notes: selectedClient.notes || [], notes_history: selectedClient.notes_history || [],
          billing_contact_name: selectedClient.billing_contact_name || "",
          billing_contact_email: selectedClient.billing_contact_email || "",
          billing_contact_phone: selectedClient.billing_contact_phone || "",
          customer_number: selectedClient.customer_number || "",
          customer_number_requested_at: selectedClient.customer_number_requested_at || "",
          tax_exempt: selectedClient.tax_exempt || false,
          hide_tax: !!selectedClient.hide_tax,
          tax_certificate_no: selectedClient.tax_certificate_no || "",
        }));
      }
    } else {
      const site = clientSites.find(s => s.id === siteId);
      if (site) {
        setFormData(prev => ({
          ...prev,
          address: site.address || "",
          address_line_2: site.address_line_2 || "",
          city: site.city || "",
          state: site.state || "",
          zip_code: site.zip_code || "",
          contacts: site.contacts || [],
          notes: site.notes || [], notes_history: site.notes_history || [],
        }));
      }
    }
    setTimeout(() => { formLoadingRef.current = false; }, 100);
  };

  // All contacts across all sites for this customer (for site contact dropdown)
  const allCustomerContacts = useMemo(() => {
    const seen = new Set();
    const contacts = [];
    for (const site of clientSites) {
      for (const c of (site.contacts || [])) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          contacts.push({ ...c, _siteName: site.name });
        }
      }
    }
    return contacts;
  }, [clientSites]);

  // Contacts visible in current view: site-specific + all primary contacts from any site
  const visibleContacts = useMemo(() => {
    let contacts;
    if (selectedSiteId === "all") {
      // Show primary contacts from ALL sites
      const allPrimaries = [];
      const seenIds = new Set();
      for (const s of clientSites) {
        for (const c of (s.contacts || [])) {
          if (c.is_primary && !seenIds.has(c.id)) {
            allPrimaries.push({ ...c, _fromSite: s.name });
            seenIds.add(c.id);
          }
        }
      }
      // Also include any client-level contacts from formData
      for (const c of (formData.contacts || [])) {
        if (!seenIds.has(c.id)) {
          allPrimaries.push(c);
          seenIds.add(c.id);
        }
      }
      contacts = allPrimaries;
    } else {
      const siteContacts = formData.contacts || [];
      // Collect primary contacts from ALL other sites and show them here
      const otherSitePrimaries = [];
      for (const s of clientSites) {
        if (s.id !== selectedSiteId) {
          for (const c of (s.contacts || [])) {
            if (c.is_primary) otherSitePrimaries.push({ ...c, _fromSite: s.name });
          }
        }
      }
      const siteIds = new Set(siteContacts.map(c => c.id));
      contacts = [...siteContacts, ...otherSitePrimaries.filter(c => !siteIds.has(c.id))];
    }
    // Primary contacts first
    return [...contacts].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
  }, [formData.contacts, selectedSiteId, clientSites]);

  const handleSaveClientInline = async () => {
    try {
      // When viewing "All Sites", formData.contacts represents the CLIENT-level contacts.
      // When viewing a specific site, formData.contacts represents that site's contacts (site
      // may also inherit some client-level contacts — those are filtered in visibleContacts).
      // Either way, we always sync the full client contacts list from formData.contacts when
      // on "all" view, so the contact is never lost.
      const clientContacts = selectedSiteId === "all"
        ? formData.contacts
        : (selectedClient.contacts || []);
      // iter241 — same client-vs-site notes rule as autoSaveInline
      const notesSite = selectedSiteId !== "all"
        ? clientSites.find(s => s.id === selectedSiteId)
        : (clientSites.length === 1 ? clientSites[0] : clientSites.find(s => s.is_primary));
      await updateClient(selectedClient.id, {
        name: formData.name,
        is_new_lead: formData.is_new_lead,
        payment_hold: formData.payment_hold,
        facility_type: selectedClient.facility_type,
        rating: selectedClient.rating || "",
        contacts: clientContacts,
        notes: notesSite ? (selectedClient.notes || []) : (formData.notes || []),
        notes_history: notesSite ? (selectedClient.notes_history || []) : (formData.notes_history || []),
        address: selectedClient.address || "",
        city: selectedClient.city || "",
        state: selectedClient.state || "",
        zip_code: selectedClient.zip_code || "",
        tax_exempt: formData.tax_exempt || false,
        hide_tax: !!formData.hide_tax,
        tax_certificate_no: formData.tax_certificate_no || "",
      });

      // Save site-level data if a site is selected
      if (selectedSiteId !== "all") {
        const site = clientSites.find(s => s.id === selectedSiteId);
        if (site) {
          await updateSite(selectedSiteId, {
            ...site,
            address: formData.address,
            address_line_2: formData.address_line_2 || "",
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code,
            contacts: formData.contacts,
            notes: formData.notes, notes_history: formData.notes_history,
          });
          setClientSites(clientSites.map(s => s.id === selectedSiteId ? {
            ...s,
            address: formData.address,
            address_line_2: formData.address_line_2 || "",
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code,
            contacts: formData.contacts,
            notes: formData.notes, notes_history: formData.notes_history,
          } : s));
        }
      } else if (primarySite) {
        // Save to primary site when on "All Sites"
        await updateSite(primarySite.id, {
          ...primarySite,
          address: formData.address,
          address_line_2: formData.address_line_2 || "",
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          contacts: formData.contacts,
          notes: formData.notes, notes_history: formData.notes_history,
        });
        setClientSites(clientSites.map(s => s.id === primarySite.id ? {
          ...s,
          address: formData.address,
          address_line_2: formData.address_line_2 || "",
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          contacts: formData.contacts,
          notes: formData.notes, notes_history: formData.notes_history,
        } : s));
      }

      const updated = { ...selectedClient, name: formData.name, is_new_lead: formData.is_new_lead };
      setSelectedClient(updated);
      setClients(clients.map(c => c.id === selectedClient.id ? { ...c, name: formData.name, is_new_lead: formData.is_new_lead } : c));
      toast.success("Customer saved");
    } catch (error) {
      toast.error(withDetail(error, "Failed to save customer"));
    }
  };

  // iter241 — archive a customer note into notes_history (History tab) and back
  const handleArchiveNote = (idx) => {
    setFormData(prev => {
      const notes = [...(prev.notes || [])];
      const [n] = notes.splice(idx, 1);
      if (!n) return prev;
      const hist = [{ ...n, archived_at: new Date().toISOString().slice(0, 10) }, ...(prev.notes_history || [])];
      return { ...prev, notes, notes_history: hist };
    });
    toast.success("Note archived to History");
  };

  const handleRestoreNote = (idx) => {
    setFormData(prev => {
      const hist = [...(prev.notes_history || [])];
      const [n] = hist.splice(idx, 1);
      if (!n) return prev;
      const { archived_at, ...rest } = n;
      return { ...prev, notes: [rest, ...(prev.notes || [])], notes_history: hist };
    });
    toast.success("Note restored");
  };

  const loadClientDetails = async (client, targetTab) => {
    formLoadingRef.current = true;
    // Edit-lock decision (UX rule):
    //   • Switching to a different customer → start LOCKED (read-only) so
    //     the user explicitly opts in to edit by clicking the pencil.
    //   • Reloading the SAME customer (autosave/refresh after adding a
    //     site/contact/etc.) → preserve the current lock state. Otherwise
    //     the sheet auto-relocks every save and the user has to keep
    //     clicking the pencil mid-flow.
    //   • New leads (is_new_lead === true) and brand-new customers always
    //     start UNLOCKED so the user can type their info without re-
    //     clicking the pencil after the first autosave.
    const isSameClient = selectedClient && selectedClient.id === client.id;
    const isFreshOrLead = !!client.is_new_lead;
    setSelectedClient(client);
    setIsSheetOpen(true);
    setSelectedSiteId("all");
    setSaveStatus(null);
    if (isSameClient) {
      // Preserve whatever the user had — don't fight the user mid-edit.
      // (sheetLocked stays as-is)
    } else {
      setEditingBillingContact(false);
      setEditingContactId(null);
      setIsAddingContact(false);
      setIsAddingNote(false);
      setSiteEditMode(false);
    }
    if (isSameClient) {
      // sheetLocked unchanged
    } else if (isFreshOrLead) {
      setSheetLocked(false);
    } else {
      setSheetLocked(true);
    }
    setActiveDetailTab(targetTab || "general");
    setIsAddingContact(isSameClient ? isAddingContact : false);
    setIsAddingNote(isSameClient ? isAddingNote : false);
    setSiteEditMode(isSameClient ? siteEditMode : false);
    setNewContact({ name: "", email: "", phone: "", role: "", notes: "", is_primary: false });
    setFormData({
      name: client.name,
      facility_type: client.facility_type || "",
      is_new_lead: client.is_new_lead || false,
      payment_hold: client.payment_hold || false,
      contacts: client.contacts || [],
      address: client.address || "",
      // #160 (Group H): hydrate the optional Unit/Apt/Suite field so a
      // subsequent save round-trips it instead of erasing it. Without
      // this load, opening a client with a populated `address_line_2`
      // and clicking Save would wipe the field because the form
      // submit sends `formData.address_line_2` (which would still be
      // the empty-string default from `initialForm`).
      address_line_2: client.address_line_2 || "",
      city: client.city || "",
      state: client.state || "",
      zip_code: client.zip_code || "",
      notes: client.notes || [], notes_history: client.notes_history || [],
      billing_contact_name: client.billing_contact_name || "",
      billing_contact_email: client.billing_contact_email || "",
      billing_contact_phone: client.billing_contact_phone || "",
      customer_number: client.customer_number || "",
      customer_number_requested_at: client.customer_number_requested_at || "",
      tax_exempt: client.tax_exempt || false,
      hide_tax: !!client.hide_tax,
      tax_certificate_no: client.tax_certificate_no || "",
    });
    
    try {
      const [sitesRes, equipRes, historyRes, proposalsRes, statsRes, manufacturersRes, typesRes, partsRes, equipNamesRes, archivedEquipRes, serviceTypesRes, workOrdersRes, masterScopesRes, masterEquipRes, masterPartsRes, inspectionsRes] = await Promise.all([
        getSites({ client_id: client.id }),
        getEquipment({ client_id: client.id }),
        getServiceHistory({ client_id: client.id }),
        getProposals({ client_id: client.id }),
        getClientStats(client.id),
        getManufacturers(),
        getEquipmentTypes(),
        getParts(),
        getEquipmentNames(client.id),
        getArchivedEquipment({ client_id: client.id }),
        getServiceTypes(),
        getWorkOrders({ client_id: client.id }),
        getMasterScopes(),
        getMasterEquipment({}),
        getMasterParts({}),
        getInspections({ client_id: client.id }),
      ]);
      setClientSites(sitesRes.data);
      setClientEquipment(equipRes.data);
      setArchivedEquipment(archivedEquipRes.data);
      setClientWorkOrders(workOrdersRes.data || []);
      setClientServiceHistory(historyRes.data);
      setClientProposals(proposalsRes.data);
      setClientStats(statsRes.data);
      setClientInspections(inspectionsRes.data || []);
      setManufacturers(manufacturersRes.data);
      setEquipmentTypes(typesRes.data);
      setParts(partsRes.data);
      setEquipmentNames(equipNamesRes.data);
      setServiceTypes(serviceTypesRes.data);
      setMasterScopes(masterScopesRes.data);
      setMasterEquipment(masterEquipRes.data);
      setMasterParts(masterPartsRes.data);
      // Load documents in background
      getClientDocuments(client.id).then(r => setClientDocuments(r.data)).catch(() => {});
      // iter241 — unified communications timeline for the History tab
      getClientCommunications(client.id).then(r => setClientCommunications(r.data || [])).catch(() => setClientCommunications([]));
      const sites = sitesRes.data;
      const ps = sites.find(s => s.is_primary);
      if (sites.length === 1) {
        setSelectedSiteId(sites[0].id);
        setFormData(prev => ({
          ...prev,
          address: sites[0].address || "",
          city: sites[0].city || "",
          state: sites[0].state || "",
          zip_code: sites[0].zip_code || "",
          contacts: sites[0].contacts || [],
          notes: sites[0].notes || [], notes_history: sites[0].notes_history || [],
        }));
      } else if (ps) {
        setSelectedSiteId(ps.id);
        setFormData(prev => ({
          ...prev,
          address: ps.address || "",
          city: ps.city || "",
          state: ps.state || "",
          zip_code: ps.zip_code || "",
          contacts: ps.contacts || [],
          notes: ps.notes || [], notes_history: ps.notes_history || [],
        }));
      }
    } catch (error) {
      toast.error("Failed to load customer details");
    }
    setTimeout(() => { formLoadingRef.current = false; }, 200);
  };

  // Navigate directly to a work order from search results — full page (UI sweep batch 5).
  const navigateToWorkOrder = (wo) => {
    setShowWoDropdown(false);
    setSearch("");
    navigate(`/work-orders/${wo.id}`);
  };

  // Computed filtered data based on selected site
  const [equipmentNameFilter, setEquipmentNameFilter] = useState("");
  const [equipmentSiteFilter, setEquipmentSiteFilter] = useState("");
  const [equipmentDescFilter, setEquipmentDescFilter] = useState("");
  const [equipmentServiceTypeFilter, setEquipmentServiceTypeFilter] = useState("");
  const [equipmentSearch, setEquipmentSearch] = useState("");
  const [equipmentSort, setEquipmentSort] = useState({ field: "name", dir: "asc" });
  const [inlineNoteEquipId, setInlineNoteEquipId] = useState(null);
  const [inlineNoteText, setInlineNoteText] = useState("");
  const [activeDetailTab, setActiveDetailTab] = useState("general");
  const [pendingWorkOrderId, setPendingWorkOrderId] = useState(null);
  const [pendingCopyWO, setPendingCopyWO] = useState(null);
  const [pendingViewWO, setPendingViewWO] = useState(null);
  const [woReturnTo, setWoReturnTo] = useState(null);
  const workOrderTabRef = useRef(null);

  const toggleEquipmentSort = (field) => {
    setEquipmentSort(prev => prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" });
  };

  const filteredEquipment = useMemo(() => {
    let list = selectedSiteId === "all" ? clientEquipment : clientEquipment.filter(e => e.site_id === selectedSiteId);
    if (equipmentNameFilter) list = list.filter(e => e.name === equipmentNameFilter);
    if (equipmentSiteFilter) list = list.filter(e => e.site_id === equipmentSiteFilter);
    if (equipmentDescFilter) list = list.filter(e => e.equipment_type_id === equipmentDescFilter);
    if (equipmentServiceTypeFilter) list = list.filter(e => e.service_type === equipmentServiceTypeFilter);
    if (equipmentSearch) {
      const q = equipmentSearch.toLowerCase();
      list = list.filter(e => {
        const siteName = (clientSites.find(s => s.id === e.site_id)?.name || "").toLowerCase();
        const descName = (equipmentTypes.find(t => t.id === e.equipment_type_id)?.name || "").toLowerCase();
        return (e.name || "").toLowerCase().includes(q) ||
          (e.location || "").toLowerCase().includes(q) ||
          siteName.includes(q) ||
          descName.includes(q) ||
          (Array.isArray(e.notes) ? e.notes.some(n => n.text.toLowerCase().includes(q)) : false) ||
          (e.last_serviced ? format(new Date(e.last_serviced), 'MMM d, yyyy').toLowerCase().includes(q) : false) ||
          (e.install_date ? format(new Date(e.install_date), 'MMM d, yyyy').toLowerCase().includes(q) : false);
      });
    }
    return [...list].sort((a, b) => {
      let aVal, bVal;
      if (equipmentSort.field === "name") { aVal = (a.name || "").toLowerCase(); bVal = (b.name || "").toLowerCase(); }
      else if (equipmentSort.field === "site") { aVal = (clientSites.find(s => s.id === a.site_id)?.name || "").toLowerCase(); bVal = (clientSites.find(s => s.id === b.site_id)?.name || "").toLowerCase(); }
      else if (equipmentSort.field === "last_serviced") { aVal = a.last_serviced || a.install_date || ""; bVal = b.last_serviced || b.install_date || ""; }
      else return 0;
      if (aVal < bVal) return equipmentSort.dir === "asc" ? -1 : 1;
      if (aVal > bVal) return equipmentSort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [clientEquipment, selectedSiteId, equipmentNameFilter, equipmentSiteFilter, equipmentDescFilter, equipmentServiceTypeFilter, equipmentSearch, equipmentSort, clientSites, equipmentTypes]);

  const uniqueEquipmentNames = useMemo(() => {
    const siteEquip = selectedSiteId === "all" ? clientEquipment : clientEquipment.filter(e => e.site_id === selectedSiteId);
    return [...new Set(siteEquip.map(e => e.name).filter(Boolean))].sort();
  }, [clientEquipment, selectedSiteId]);

  const uniqueEquipmentSites = useMemo(() => {
    const siteEquip = selectedSiteId === "all" ? clientEquipment : clientEquipment.filter(e => e.site_id === selectedSiteId);
    const siteIds = [...new Set(siteEquip.map(e => e.site_id).filter(Boolean))];
    return siteIds.map(id => clientSites.find(s => s.id === id)).filter(Boolean);
  }, [clientEquipment, selectedSiteId, clientSites]);

  const uniqueEquipmentDescs = useMemo(() => {
    const siteEquip = selectedSiteId === "all" ? clientEquipment : clientEquipment.filter(e => e.site_id === selectedSiteId);
    const typeIds = [...new Set(siteEquip.map(e => e.equipment_type_id).filter(Boolean))];
    return typeIds.map(id => equipmentTypes.find(t => t.id === id)).filter(Boolean);
  }, [clientEquipment, selectedSiteId, equipmentTypes]);

  const filteredProposals = useMemo(() => {
    if (selectedSiteId === "all") return clientProposals;
    return clientProposals.filter(p => p.site_id === selectedSiteId);
  }, [clientProposals, selectedSiteId]);

  const filteredHistory = useMemo(() => {
    if (selectedSiteId === "all") return clientServiceHistory;
    return clientServiceHistory.filter(j => j.site_id === selectedSiteId);
  }, [clientServiceHistory, selectedSiteId]);

  const filteredArchivedEquipment = useMemo(() => {
    let list = selectedSiteId === "all" ? archivedEquipment : archivedEquipment.filter(e => e.site_id === selectedSiteId);
    return [...list].sort((a, b) => {
      const aDate = a.last_serviced || a.archived_at || "";
      const bDate = b.last_serviced || b.archived_at || "";
      return bDate > aDate ? 1 : bDate < aDate ? -1 : 0;
    });
  }, [archivedEquipment, selectedSiteId]);

  const completedWorkOrders = useMemo(() => clientWorkOrders.filter(wo => wo.status === "completed"), [clientWorkOrders]);
  const cancelledWorkOrders = useMemo(() => clientWorkOrders.filter(wo => wo.status === "cancelled"), [clientWorkOrders]);
  const wonProposals = useMemo(() => clientProposals.filter(p => p.status === "won"), [clientProposals]);
  const lostProposals = useMemo(() => clientProposals.filter(p => p.status === "lost"), [clientProposals]);

  // KPI calculations — moved INTO `<CustomerKPIStrip>` (iter190) so
  // the strip owns the MTD / YTD / 12m / All-time window picker.


  const selectedSite = useMemo(() => {
    if (selectedSiteId === "all") return null;
    return clientSites.find(s => s.id === selectedSiteId);
  }, [clientSites, selectedSiteId]);

  const getSiteNameById = (siteId) => {
    if (!siteId) return "";
    const site = clientSites.find(s => s.id === siteId);
    return site ? site.name : "";
  };

  const handleSetPrimary = async (siteId) => {
    if (!siteId) return;
    try {
      const site = clientSites.find(s => s.id === siteId);
      if (!site) return;
      await updateSite(siteId, { ...site, is_primary: true });
      // Unset primary on all other sites
      for (const s of clientSites) {
        if (s.id !== siteId && s.is_primary) {
          await updateSite(s.id, { ...s, is_primary: false });
        }
      }
      setClientSites(prev => prev.map(s => ({ ...s, is_primary: s.id === siteId })));
      toast.success(`${site.name} set as primary`);
    } catch (error) {
      toast.error(withDetail(error, "Failed to update primary site"));
    }
  };

  const [renamingSite, setRenamingSite] = useState(false);
  const [siteRenameValue, setSiteRenameValue] = useState("");
  const [deleteSiteConfirmOpen, setDeleteSiteConfirmOpen] = useState(false);

  const handleRenameSite = async () => {
    if (!siteRenameValue.trim() || !selectedSite) return;
    try {
      await updateSite(selectedSiteId, { ...selectedSite, name: siteRenameValue.trim() });
      setClientSites(prev => prev.map(s => s.id === selectedSiteId ? { ...s, name: siteRenameValue.trim() } : s));
      setRenamingSite(false);
      toast.success("Site renamed");
    } catch (error) {
      toast.error(withDetail(error, "Failed to rename site"));
    }
  };

  const handleDeleteSite = async () => {
    if (!selectedSite) return;
    try {
      await deleteSite(selectedSiteId);
      const remaining = clientSites.filter(s => s.id !== selectedSiteId);
      setClientSites(remaining);
      formLoadingRef.current = true;
      setDeleteSiteConfirmOpen(false);
      if (remaining.length > 0) {
        const newPrimary = remaining.find(s => s.is_primary) || remaining[0];
        switchSiteData(newPrimary.id);
      } else {
        switchSiteData("all");
      }
      setTimeout(() => { formLoadingRef.current = false; }, 200);
      toast.success("Site deleted");
    } catch (error) {
      toast.error(withDetail(error, "Failed to delete site"));
    }
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    formLoadingRef.current = true;
    try {
      // Build contacts array — if adding a new contact, include it
      const siteContacts = [];
      if (siteContactMode === "new" && siteFormData.contact_name.trim()) {
        siteContacts.push({
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
          name: siteFormData.contact_name.trim(),
          phone: siteFormData.contact_phone || "",
          email: siteFormData.contact_email || "",
          is_primary: false,
        });
      }
      const res = await createSite({
        ...siteFormData,
        client_id: selectedClient.id,
        contacts: siteContacts,
        notes: [],
      });
      const newSite = res.data;
      // If this site is primary, update other sites locally
      let updatedSites;
      if (newSite.is_primary) {
        updatedSites = clientSites.map(s => ({ ...s, is_primary: false }));
        updatedSites.push(newSite);
      } else {
        updatedSites = [...clientSites, newSite];
      }
      setClientSites(updatedSites);
      // Set formData directly from new site (don't rely on stale clientSites)
      setSelectedSiteId(newSite.id);
      setFormData(prev => ({
        ...prev,
        address: newSite.address || "",
        city: newSite.city || "",
        state: newSite.state || "",
        zip_code: newSite.zip_code || "",
        contacts: newSite.contacts || [],
        notes: newSite.notes || [],
      }));
      setIsSiteDialogOpen(false);
      setSiteFormData({ name: "", address: "", city: "", state: "", zip_code: "", facility_type: "", is_primary: false, tax_authority: "", contact_name: "", contact_phone: "", contact_email: "" });
      setSiteContactMode("existing");
      toast.success("Site added");
    } catch (error) {
      toast.error(withDetail(error, "Failed to add site"));
    }
    setTimeout(() => { formLoadingRef.current = false; }, 100);
  };

  // Equipment handlers
  const loadEquipmentTypes = async (manufacturerId) => {
    if (manufacturerId) {
      try {
        const response = await getEquipmentTypes(manufacturerId);
        setFilteredTypes(response.data);
      } catch (error) {
        setFilteredTypes([]);
      }
    } else {
      setFilteredTypes([]);
    }
    setFilteredParts([]);
  };

  const loadParts = async (equipmentTypeId) => {
    if (equipmentTypeId) {
      try {
        const response = await getParts(equipmentTypeId);
        setFilteredParts(response.data);
      } catch (error) {
        setFilteredParts([]);
      }
    } else {
      setFilteredParts([]);
    }
  };

  const handleAddManufacturer = async () => {
    if (newManufacturer.trim()) {
      try {
        const response = await createManufacturer({ name: newManufacturer.trim() });
        setManufacturers([...manufacturers, response.data]);
        setEquipmentFormData({ ...equipmentFormData, manufacturer_id: response.data.id });
        setNewManufacturer("");
        setIsAddingManufacturer(false);
        loadEquipmentTypes(response.data.id);
        toast.success("Vendor added");
      } catch (error) {
        toast.error("Vendor already exists");
      }
    }
  };

  const handleAddEquipmentType = async () => {
    if (newType.trim() && equipmentFormData.manufacturer_id) {
      try {
        const response = await createEquipmentType({ 
          name: newType.trim(), 
          manufacturer_id: equipmentFormData.manufacturer_id 
        });
        setFilteredTypes([...filteredTypes, response.data]);
        setEquipmentTypes([...equipmentTypes, response.data]);
        setEquipmentFormData({ ...equipmentFormData, equipment_type_id: response.data.id });
        setNewType("");
        setIsAddingType(false);
        loadParts(response.data.id);
        toast.success("Description added");
      } catch (error) {
        toast.error("Description already exists for this manufacturer");
      }
    }
  };

  const handleAddPart = async () => {
    if (newPart.trim() && equipmentFormData.equipment_type_id) {
      try {
        const response = await createPart({ 
          name: newPart.trim(), 
          equipment_type_id: equipmentFormData.equipment_type_id 
        });
        setFilteredParts([...filteredParts, response.data]);
        setParts([...parts, response.data]);
        setEquipmentFormData({ ...equipmentFormData, part_id: response.data.id });
        setNewPart("");
        setIsAddingPart(false);
        toast.success("Part added");
      } catch (error) {
        toast.error("Part already exists for this type");
      }
    }
  };

  const handleAddEquipmentName = async () => {
    if (newEquipmentName.trim()) {
      try {
        const response = await createEquipmentName({ name: newEquipmentName.trim(), client_id: selectedClient?.id });
        setEquipmentNames([...equipmentNames, response.data]);
        setEquipmentFormData({ ...equipmentFormData, name: response.data.name });
        setNewEquipmentName("");
        setIsAddingEquipmentName(false);
        toast.success("Equipment name added");
      } catch (error) {
        toast.error("Equipment name already exists");
      }
    }
  };

  const handleAddServiceType = async () => {
    if (newServiceType.trim()) {
      try {
        const response = await createServiceType({ name: newServiceType.trim() });
        setServiceTypes([...serviceTypes, response.data]);
        setEquipmentFormData({ ...equipmentFormData, service_type: response.data.name });
        setNewServiceType("");
        setIsAddingServiceType(false);
        toast.success("Service type added");
      } catch (error) {
        toast.error("Service type already exists");
      }
    }
  };

  const handleEquipmentSubmit = async (e, addAnother = false) => {
    e.preventDefault();
    try {
      const submitData = {
        ...equipmentFormData,
        client_id: selectedClient.id,
        site_id: equipmentFormData.site_id || null,
        install_date: equipmentFormData.install_date ? equipmentFormData.install_date.toISOString() : null,
        last_serviced: equipmentFormData.last_serviced ? equipmentFormData.last_serviced.toISOString() : null,
      };
      
      if (editingEquipment) {
        await updateEquipment(editingEquipment.id, submitData);
        toast.success("Equipment updated");
      } else {
        await createEquipment(submitData);
        // Sync to master library if enabled
        if (saveToLibrary) {
          try { await syncToMasterLibrary(equipmentFormData); } catch {}
        }
        toast.success("Equipment added");
      }
      
      // Reload equipment list
      const equipRes = await getEquipment({ client_id: selectedClient.id });
      setClientEquipment(equipRes.data);

      if (addAnother && !editingEquipment) {
        // Keep scope, site, manufacturer, service type — reset item-specific fields
        setEquipmentFormData(prev => ({
          ...prev,
          name: "",
          part_name: "",
          part_number: "",
          description: "",
          part_cost: 0,
          material_margin: 50,
          freight: 0,
          labor_hours: 0,
          labor_cost: 40,
          labor_margin: 73.333,
          equipment_cost: 0,
          equipment_margin: 25,
          disposal_cost: 0,
          disposal_margin: 25,
          travel_cost: 0,
          travel_margin: 25,
          qty: 1,
          notes: [],
          equipment_type_id: "",
          part_id: "",
          install_date: null,
          last_serviced: null,
        }));
        setFilteredParts([]);
        setImportedFromLibrary(false);
        setLibrarySearch("");
      } else {
        setIsEquipmentDialogOpen(false);
        resetEquipmentForm();
      }
    } catch (error) {
      toast.error("Failed to save equipment");
    }
  };

  const handleEditEquipment = async (equip) => {
    setEditingEquipment(equip);
    
    // Load cascading dropdowns for the form
    if (equip.manufacturer_id) {
      await loadEquipmentTypes(equip.manufacturer_id);
    }
    if (equip.equipment_type_id) {
      await loadParts(equip.equipment_type_id);
    }
    
    setEquipmentFormData({
      name: equip.name,
      site_id: equip.site_id || "",
      manufacturer_id: equip.manufacturer_id || "",
      equipment_type_id: equip.equipment_type_id || "",
      part_id: equip.part_id || "",
      service_type: equip.service_type || "",
      install_date: equip.install_date ? new Date(equip.install_date) : null,
      last_serviced: equip.last_serviced ? new Date(equip.last_serviced) : null,
      notes: Array.isArray(equip.notes) ? equip.notes : [],
      scope_name: equip.scope_name || "",
      manufacturer_name: equip.manufacturer_name || "",
      part_name: equip.part_name || "",
      part_number: equip.part_number || "",
      description: equip.description || "",
      part_cost: equip.part_cost || 0,
      material_margin: equip.material_margin ?? 50,
      freight: equip.freight || 0,
      labor_hours: equip.labor_hours || 0,
      labor_cost: equip.labor_cost ?? 40,
      labor_margin: equip.labor_margin ?? 73.333,
      equipment_cost: equip.equipment_cost || 0,
      equipment_margin: equip.equipment_margin ?? 25,
      disposal_cost: equip.disposal_cost || 0,
      disposal_margin: equip.disposal_margin ?? 25,
      travel_cost: equip.travel_cost || 0,
      travel_margin: equip.travel_margin ?? 25,
      qty: equip.qty || 1,
      location: equip.location || "",
      cost_code: equip.cost_code || "20-100000",
      department: equip.department || "Service",
    });
    setIsEquipmentDialogOpen(true);
  };

  const resetEquipmentForm = () => {
    setEditingEquipment(null);
    setLibrarySearch("");
    setSaveToLibrary(true);
    setImportedFromLibrary(false);
    const labCost = appSettings?.default_labor_cost ?? 40;
    const labMargin = appSettings?.default_labor_margin ?? 73.333;
    const matMargin = appSettings?.default_material_margin ?? 50;
    setEquipmentFormData({
      name: "",
      site_id: selectedSiteId !== "all" ? selectedSiteId : "",
      manufacturer_id: "",
      equipment_type_id: "",
      part_id: "",
      service_type: "",
      install_date: null,
      last_serviced: null,
      notes: [],
      scope_name: "",
      manufacturer_name: "",
      part_name: "",
      part_number: "",
      description: "",
      part_cost: 0,
      material_margin: matMargin,
      freight: 0,
      labor_hours: 0,
      labor_cost: labCost,
      labor_margin: labMargin,
      equipment_cost: 0,
      equipment_margin: 25,
      disposal_cost: 0,
      disposal_margin: 25,
      travel_cost: 0,
      travel_margin: 25,
      qty: 1,
    });
    setFilteredTypes([]);
    setFilteredParts([]);
    setIsAddingManufacturer(false);
    setIsAddingType(false);
    setIsAddingPart(false);
    setIsAddingEquipmentName(false);
    setIsAddingServiceType(false);
  };

  // Build library search options: combine master equipment and parts into one searchable list
  const libraryOptions = useMemo(() => {
    const selectedScopeId = masterScopes.find(s => s.name === equipmentFormData.scope_name)?.id;
    const options = [];
    masterEquipment
      .filter(me => !selectedScopeId || me.scope_id === selectedScopeId)
      .forEach(me => {
        const scope = masterScopes.find(s => s.id === me.scope_id);
        options.push({
          value: `equip:${me.id}`,
          label: `${me.name}${scope ? ` (${scope.name})` : ""}`,
          equipName: me.name,
        });
      });
    masterParts
      .filter(mp => !selectedScopeId || mp.scope_id === selectedScopeId)
      .forEach(mp => {
        const equip = masterEquipment.find(e => e.id === mp.equipment_id);
        const scope = masterScopes.find(s => s.id === mp.scope_id);
        const prefix = equip ? `${equip.name} > ` : "";
        const suffix = scope ? ` (${scope.name})` : "";
        options.push({
          value: `part:${mp.id}`,
          label: `${prefix}${mp.name}${mp.part_number ? ` [${mp.part_number}]` : ""}${suffix}`,
          equipName: equip?.name || "",
          partName: mp.name,
        });
      });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [masterEquipment, masterParts, masterScopes, equipmentFormData.scope_name]);

  // Vendors available for the selected scope (derived from Equipment Library equipment + parts)
  const scopeManufacturers = useMemo(() => {
    const scopeId = masterScopes.find(s => s.name === equipmentFormData.scope_name)?.id;
    if (!scopeId) return [];
    const equipMfgs = masterEquipment
      .filter(e => e.scope_id === scopeId && e.manufacturer)
      .map(e => e.manufacturer);
    const scopeEquipIds = masterEquipment.filter(e => e.scope_id === scopeId).map(e => e.id);
    const partMfgs = masterParts
      .filter(p => scopeEquipIds.includes(p.equipment_id) && p.manufacturer)
      .map(p => p.manufacturer);
    return [...new Set([...equipMfgs, ...partMfgs])].sort();
  }, [masterEquipment, masterParts, masterScopes, equipmentFormData.scope_name]);

  // When user picks from library, auto-fill ALL equipment/part fields
  const handleLibrarySelect = (val) => {
    if (!val || val === "none") { setLibrarySearch(""); return; }
    const [type, id] = val.split(":");
    if (type === "equip") {
      const me = masterEquipment.find(e => e.id === id);
      if (me) {
        const scope = masterScopes.find(s => s.id === me.scope_id);
        setImportedFromLibrary(true);
        setSaveToLibrary(false);
        setEquipmentFormData(prev => ({
          ...prev,
          name: me.name || prev.name || "",
          scope_name: scope?.name || "",
          manufacturer_name: me.manufacturer || "",
          description: me.description || "",
        }));
        if (!equipmentNames.find(en => en.name === me.name)) {
          setEquipmentNames(prev => [...prev, { name: me.name, id: me.id }]);
        }
      }
    } else if (type === "part") {
      const mp = masterParts.find(p => p.id === id);
      if (mp) {
        const equip = masterEquipment.find(e => e.id === mp.equipment_id);
        const scope = masterScopes.find(s => s.id === mp.scope_id);
        setImportedFromLibrary(true);
        setSaveToLibrary(false);
        setEquipmentFormData(prev => ({
          ...prev,
          name: mp.name || prev.name || "",
          scope_name: scope?.name || "",
          manufacturer_name: equip?.manufacturer || mp.manufacturer || "",
          part_name: mp.name || "",
          part_number: mp.part_number || "",
          description: mp.description || "",
          part_cost: mp.part_cost || 0,
          material_margin: mp.material_margin ?? 50,
          freight: mp.freight || 0,
          labor_hours: mp.labor_hours || 0,
          labor_cost: mp.labor_cost ?? 40,
          labor_margin: mp.labor_margin ?? 73.333,
          qty: 1,
        }));
        if (equip && !equipmentNames.find(en => en.name === equip.name)) {
          setEquipmentNames(prev => [...prev, { name: equip.name, id: equip.id }]);
        }
      }
    }
    setLibrarySearch("");
  };

  // Sync new equipment to master library on save
  const syncToMasterLibrary = async (formData) => {
    const equipName = formData.name?.trim();
    const scopeName = formData.scope_name?.trim();
    const vendor = formData.vendor?.trim();
    const partNumber = formData.part_number?.trim();
    if (!equipName || !scopeName || !vendor || !partNumber) {
      toast.error("Scope, Vendor, Name, and Part Number are required to save to library");
      return;
    }
    // Check if equipment name already exists in master library
    const exists = masterEquipment.find(me => me.name.toLowerCase() === equipName.toLowerCase());
    if (exists) return;
    // Find the matching scope or create one
    let scopeId = masterScopes.find(s => s.name.toLowerCase() === scopeName.toLowerCase())?.id;
    if (!scopeId) {
      const res = await createMasterScope({ name: scopeName });
      scopeId = res.data.id;
    }
    await createMasterEquipment({ name: equipName, scope_id: scopeId, description: "" });
    toast.success("Saved to Equipment Library");
  };

  // Inline equipment field update (auto-save single field)
  const handleInlineEquipmentUpdate = async (equipId, field, value) => {
    try {
      const equip = clientEquipment.find(e => e.id === equipId);
      if (!equip) return;
      setClientEquipment(prev => prev.map(e => e.id === equipId ? { ...e, [field]: value } : e));
      const submitData = {
        name: equip.name,
        site_id: equip.site_id || null,
        client_id: selectedClient.id,
        manufacturer_id: equip.manufacturer_id || null,
        equipment_type_id: equip.equipment_type_id || null,
        part_id: equip.part_id || null,
        service_type: equip.service_type || null,
        install_date: equip.install_date || null,
        last_serviced: equip.last_serviced || null,
        notes: equip.notes || [],
        [field]: value,
      };
      await updateEquipment(equipId, submitData);
    } catch (error) {
      toast.error("Failed to update");
      const equipRes = await getEquipment({ client_id: selectedClient.id });
      setClientEquipment(equipRes.data);
    }
  };

  // Quick add equipment inline
  const handleQuickAddEquipment = async (name) => {
    if (!name) return;
    try {
      const submitData = {
        name,
        client_id: selectedClient.id,
        site_id: selectedSiteId !== "all" ? selectedSiteId : null,
        notes: [],
      };
      await createEquipment(submitData);
      const equipRes = await getEquipment({ client_id: selectedClient.id });
      setClientEquipment(equipRes.data);
      toast.success("Equipment added");
    } catch (error) {
      toast.error("Failed to add equipment");
    }
  };

  // Inline note add for equipment
  const handleInlineNoteAdd = async (equipId, noteText) => {
    if (!noteText.trim()) return;
    try {
      const equip = clientEquipment.find(e => e.id === equipId);
      if (!equip) return;
      const today = getMountainToday();
      const newNote = { date: today, text: noteText.trim() };
      const updatedNotes = [newNote, ...(Array.isArray(equip.notes) ? equip.notes : [])];
      setClientEquipment(prev => prev.map(e => e.id === equipId ? { ...e, notes: updatedNotes } : e));
      const submitData = {
        name: equip.name,
        site_id: equip.site_id || null,
        client_id: selectedClient.id,
        manufacturer_id: equip.manufacturer_id || null,
        equipment_type_id: equip.equipment_type_id || null,
        part_id: equip.part_id || null,
        service_type: equip.service_type || null,
        install_date: equip.install_date || null,
        last_serviced: equip.last_serviced || null,
        notes: updatedNotes,
      };
      await updateEquipment(equipId, submitData);
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
      const equipRes = await getEquipment({ client_id: selectedClient.id });
      setClientEquipment(equipRes.data);
    }
  };

  const getManufacturerName = (id) => {
    const m = manufacturers.find(m => m.id === id);
    return m ? m.name : "-";
  };

  const getEquipmentTypeName = (id) => {
    const t = equipmentTypes.find(t => t.id === id);
    return t ? t.name : "-";
  };

  // Iter170 cleanup: `handleSubmit`, `resetForm`, `toggleContactPrimary`,
  // `editingClient` state, and `isDialogOpen` state were all removed
  // as dead code. The only consumer was the AddEditClientDialog,
  // which itself had no UI entry path (the row-level "Edit" context
  // menu was deleted in a previous iteration in favor of in-place
  // editing in the customer side-sheet, which uses `formData` +
  // `selectedClient` + auto-save).



  // Instant create: make customer immediately and open detail sheet
  const handleInstantCreate = async () => {
    try {
      const clientRes = await createClient({ name: "New Customer", is_new_lead: true });
      const newClient = clientRes.data;
      await createSite({
        client_id: newClient.id,
        name: "Main Office",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        facility_type: "",
        is_primary: true,
        contacts: [],
        notes: [],
      });
      await loadClients();
      // Open the detail sheet for the new customer
      loadClientDetails(newClient);
      // Focus the name field after a short delay
      setTimeout(() => {
        const nameInput = document.querySelector('[data-testid="input-detail-name"]');
        if (nameInput) { nameInput.focus(); nameInput.select(); }
      }, 500);
    } catch (error) {
      toast.error("Failed to create customer");
    }
  };

  const handleArchive = async (id) => {
    try {
      await archiveClient(id);
      toast.success("Customer archived");
      setIsSheetOpen(false);
      setSelectedClient(null);
      loadClients();
    } catch (error) {
      toast.error("Failed to archive customer");
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreClient(id);
      toast.success("Customer restored");
      loadClients();
    } catch (error) {
      toast.error("Failed to restore customer");
    }
  };

  const handlePermanentDelete = async () => {
    if (!deleteConfirmClient) return;
    try {
      await deleteClient(deleteConfirmClient.id);
      toast.success("Customer permanently deleted");
      setDeleteConfirmClient(null);
      loadClients();
    } catch { toast.error("Failed to delete customer"); }
  };


  const addContact = async () => {
    if (!newContact.name.trim()) return;
    const contact = { ...newContact, id: Date.now().toString() };
    const nextContacts = [...formData.contacts, contact];
    setFormData({ ...formData, contacts: nextContacts });
    setNewContact({ name: "", email: "", phone: "", role: "", notes: "", is_primary: false });
    setIsAddingContact(false);

    // Immediately persist to the client record so every tab (Proposals, WO, etc.)
    // sees the new contact without the user having to remember to click Save.
    if (selectedClient?.id) {
      try {
        // Persist the merged client.contacts list to backend.
        // Always go to the CLIENT so it's available everywhere; the primary site will
        // inherit via the GET /clients enrichment fallback.
        const clientContacts = [...(selectedClient.contacts || []), contact];
        await updateClient(selectedClient.id, {
          name: formData.name,
          is_new_lead: formData.is_new_lead,
          payment_hold: formData.payment_hold,
          facility_type: selectedClient.facility_type,
          rating: selectedClient.rating || "",
          contacts: clientContacts,
          notes: selectedClient.notes || [], notes_history: selectedClient.notes_history || [],
          address: selectedClient.address || "",
          city: selectedClient.city || "",
          state: selectedClient.state || "",
          zip_code: selectedClient.zip_code || "",
          tax_exempt: formData.tax_exempt || false,
        hide_tax: !!formData.hide_tax,
        tax_certificate_no: formData.tax_certificate_no || "",
        });
        // Also push to the primary site contacts so the enrichment returns it.
        const primary = clientSites.find(s => s.is_primary) || clientSites[0];
        if (primary) {
          const siteContacts = [...(primary.contacts || []), contact];
          await updateSite(primary.id, { ...primary, contacts: siteContacts });
        }
        setSelectedClient(c => ({ ...c, contacts: clientContacts }));
        toast.success(`${contact.name} added to customer contacts`);
      } catch (e) {
        toast.error("Contact saved locally — click Save to persist");
      }
    }
  };

  const removeContact = (contactId) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter(c => c.id !== contactId)
    });
    setEditingContactId(null);
  };

  const updateContact = (contactId, field, value) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.map(c => c.id === contactId ? { ...c, [field]: value } : c)
    });
  };

  // Iter170 cleanup: `toggleContactPrimary` removed — only consumer
  // was the deleted AddEditClientDialog. The side-sheet edit flow
  // does its own primary toggle inline.


  const handleAddFacilityType = async () => {
    if (newFacilityType.trim()) {
      try {
        await createFacilityType({ name: newFacilityType.trim() });
        await loadFacilityTypes();
        setFormData({ ...formData, facility_type: newFacilityType.trim() });
        setNewFacilityType("");
        setIsAddingFacilityType(false);
        toast.success("Facility type added");
      } catch (error) {
        toast.error("Facility type already exists");
      }
    }
  };

  const handleAddProposal = async (e) => {
    e.preventDefault();
    try {
      await createProposal({
        ...proposalFormData,
        client_id: selectedClient.id,
        site_id: proposalFormData.site_id || null,
      });
      toast.success("Proposal added successfully");
      setIsProposalDialogOpen(false);
      setProposalFormData({ name: "", site_id: "", value: 0, status: "draft" });
      // Reload client details
      loadClientDetails(selectedClient);
    } catch (error) {
      toast.error("Failed to add proposal");
    }
  };

  const getPrimaryContact = (contacts) => {
    if (!contacts || contacts.length === 0) return null;
    return contacts.find(c => c.is_primary) || contacts[0];
  };

  return (
    <div data-testid="clients-page" className="flex flex-col gap-2">
      <StickyTop testId="clients-sticky-top">
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="clients-toolbar">
        <SearchBar value={search} onChange={setSearch} testId="search-clients" width="w-72"
          onFocus={() => { if (woSearchResults.length > 0) setShowWoDropdown(true); }}
          onBlur={() => setTimeout(() => setShowWoDropdown(false), 200)}>
          {showWoDropdown && woSearchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="wo-search-dropdown">
              <div className="px-3 py-1.5 text-[11px] font-semibold text-slate-500 border-b">Work orders</div>
              {woSearchResults.map(wo => (
                <button
                  key={wo.id}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-b border-slate-50 last:border-0"
                  onMouseDown={(e) => { e.preventDefault(); navigateToWorkOrder(wo); }}
                  data-testid={`wo-search-result-${wo.id}`}
                >
                  <span className="text-xs font-semibold text-brand shrink-0">{wo.wo_number}</span>
                  <span className="text-xs text-slate-600 truncate flex-1">{wo.title || "Untitled"}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">{wo.status}</span>
                </button>
              ))}
            </div>
          )}
        </SearchBar>

        <PillRail testId="clients-view-pills">
          {[["facility", "Facility"], ["location", "Location"], ["rating", "Rating"]].map(([k, l]) => (
            <Pill key={k} on={groupBy === k} testId={`clients-group-${k}`} title={groupBy === k ? "Back to one list" : `Group customers by ${l.toLowerCase()}`}
              onClick={() => { setGroupBy(groupBy === k ? "" : k); setShowArchived(false); }}>{l}</Pill>
          ))}
          <PillDivider />
          <Pill on={showArchived} testId="btn-toggle-archived" title={showArchived ? "Back to active customers" : "Show archived customers"}
            onClick={() => { setShowArchived(!showArchived); setGroupBy(""); }}>Archived</Pill>
        </PillRail>

        <button type="button" onClick={handleInstantCreate} className={`${btn("write")} px-2`} title="Create a new customer and open it" aria-label="Add a new customer" data-testid="btn-add-client">
          <Plus className="w-4 h-4" />
        </button>
        {/* Iter170 cleanup: AddEditClientDialog removed as dead
            code (the row-level "Edit" entry point was deleted in
            a previous iteration in favor of in-place editing in
            the customer side-sheet, with auto-save on every
            field). `handleInstantCreate` above is the only
            customer-create entry now. The extracted dialog file
            is also deleted. */}

        <span className="ml-auto text-xs text-slate-500 num" data-testid="clients-count">
          {showArchived
            ? `${archivedClients.length} archived ${archivedClients.length === 1 ? "customer" : "customers"}`
            : `${totalCount ?? sortedClients.length} ${(totalCount ?? sortedClients.length) === 1 ? "customer" : "customers"}`}
        </span>
      </div>
      </StickyTop>

      <div className="data-table-container">
        {!showArchived && (<>
        <CustomerListTable
          loading={loading}
          sortedClients={sortedClients}
          groupBy={groupBy}
          getPrimaryContact={getPrimaryContact}
          handleSort={handleSort}
          sortField={sortField}
          sortDirection={sortDirection}
          facilityTypeFilter={facilityTypeFilter}
          setFacilityTypeFilter={setFacilityTypeFilter}
          facilityTypes={facilityTypes}
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          uniqueLocations={uniqueLocations}
          ratingFilter={ratingFilter}
          setRatingFilter={setRatingFilter}
          handleRatingChange={handleRatingChange}
          setEditDialogClient={setEditDialogClient}
          loadClientDetails={loadClientDetails}
        />

        {/* Infinite-scroll sentinel + state row. The IntersectionObserver
            attached above triggers the next page fetch when this row
            scrolls into view (200px rootMargin so it pre-loads). */}
        {!loading && clients.length > 0 && (
          <div
            ref={loadSentinelRef}
            data-testid="clients-infinite-sentinel"
            className="py-3 px-4 text-center text-[12px] text-slate-500 border-t border-slate-100"
          >
            {loadingMore ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-3 w-3 rounded-full border-2 border-slate-300 border-t-slate-600 animate-spin" />
                Loading more customers…
              </span>
            ) : hasMore ? (
              <span className="text-slate-500">Scroll for more · showing {clients.length}</span>
            ) : (
              <span className="text-slate-500">{clients.length} {clients.length === 1 ? "customer" : "customers"}</span>
            )}
          </div>
        )}
        </>)}

        {/* Archived Customers — extracted to
            `pages/clients/sections/ArchivedCustomersSection.jsx`. UI sweep: it is the "Archived" view. */}
        {showArchived && (
          <ArchivedCustomersSection
            archivedClients={archivedClients}
            handleRestore={handleRestore}
            setDeleteConfirmClient={setDeleteConfirmClient}
          />
        )}
        {/* Searching looks across archived customers too — matches surface under the active list. */}
        {!showArchived && search.trim() && archivedClients.length > 0 && (
          <div className="border-t border-slate-200 mt-3" data-testid="archived-search-matches">
            <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Archived matches · {archivedClients.length}</div>
            <ArchivedCustomersSection archivedClients={archivedClients} handleRestore={handleRestore} setDeleteConfirmClient={setDeleteConfirmClient} />
          </div>
        )}
      </div>

      {/* Permanent Delete Confirmation — extracted to
          `pages/clients/dialogs/PermanentDeleteDialog.jsx`. */}
      <PermanentDeleteDialog
        client={deleteConfirmClient}
        onClose={() => setDeleteConfirmClient(null)}
        onConfirm={handlePermanentDelete}
      />

      {/* Customer Detail Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={async (open) => {
        if (!open) {
          // iter253 — Flush any pending debounced autosave BEFORE the sheet
          // closes. Previously, edits typed in the last 800ms window were
          // silently discarded when the user hit X / Esc / clicked outside
          // — a big contributor to the "put in all of the info and it
          // didn't save" reports. `flushSaveClient` awaits the in-flight
          // save so the drawer only actually closes after the server has
          // acknowledged the write.
          try { await flushSaveClient(); } catch { /* toast already shown */ }
        }
        setIsSheetOpen(open);
        if (!open) {
          setSheetLocked(true);
          setEditingBillingContact(false);
          setEditingContactId(null);
          setIsAddingContact(false);
          setIsAddingNote(false);
          setSiteEditMode(false);
        }
        if (!open && woReturnTo) { navigate(woReturnTo); setWoReturnTo(null); }
      }}>
        {/* #182: width is centrally managed by the shared `sheet`
            component (75vw on md+, full-width on phones). The
            previous lg/xl/2xl bespoke breakpoints made this drawer
            inconsistent with every other slideout in the app. */}
        <SheetContent className="overflow-y-auto [&>button.absolute]:hidden" onEscapeKeyDown={guardEscapeKeyDown} onOpenAutoFocus={(e) => e.preventDefault()}>
          <ClientHeaderCard
            formData={formData}
            setFormData={setFormData}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            setClients={setClients}
            saveStatus={saveStatus}
            clientSites={clientSites}
            handleRatingChange={handleRatingChange}
            onArchive={() => setArchiveConfirmOpen(true)}
          />
          
          {selectedClient && (
            <div className="mt-4">
              {/* Parts Counter — completely different view */}
              {selectedClient.customer_type === "parts_counter" ? (
                <PartsCounter />
              ) : (
              <>
              {/* Top tabs (WO pattern): General first, then the record tabs. Radix Tabs root drives the TabsContent panels. */}
              <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab} className="w-full">
                <SubTabs testId="customer-detail-tabs" active={activeDetailTab} onChange={setActiveDetailTab} className="!px-0" tabs={[
                  { key: "general", label: "Info", testId: "tab-general" },
                  { key: "equipment", label: "Equipment", testId: "tab-equipment" },
                  { key: "proposals", label: "Proposals", testId: "tab-proposals" },
                  { key: "workorders", label: "Work Orders", testId: "tab-work-orders" },
                  { key: "inspections", label: "Inspections", testId: "tab-inspections" },
                  { key: "history", label: "History", testId: "tab-history" },
                ]} />

                <ClientGeneralTab
                  general={{
                    selectedClient, setSelectedClient, formData, setFormData, clients, setClients,
                    clientSites, selectedSite, sheetLocked, setSheetLocked, facilityTypes,
                    handleFacilityTypeChange, loadClients,
                  }}
                  sites={{
                    clientSites, selectedSiteId, switchSiteData, handleSetPrimary,
                    renamingSite, setRenamingSite, siteRenameValue, setSiteRenameValue, handleRenameSite,
                    setIsSiteDialogOpen, setDeleteSiteConfirmOpen,
                  }}
                  directory={{
                    selectedClient, formData, setFormData, clientSites, setClientSites, selectedSiteId, primarySite,
                    editingBillingContact, setEditingBillingContact, formLoadingRef,
                    visibleContacts, editingContactId, setEditingContactId, isAddingContact, setIsAddingContact,
                    newContact, setNewContact, contactRoles, updateContact, removeContact, addContact, addContactRole,
                  }}
                  revenue={{ workOrders: clientWorkOrders, proposals: clientProposals }}
                  notes={{
                    notes: formData.notes,
                    onChange: (updated) => setFormData({ ...formData, notes: updated }),
                    isAddingNote, setIsAddingNote,
                    onArchive: handleArchiveNote,
                  }}
                />
                
                {/* Equipment tab extracted to `pages/clients/tabs/ClientEquipmentTab.jsx`
                    (iter170 component split step #3). Owns the search bar,
                    Add/Edit Equipment dialog, and the equipment list table. */}
                <ClientEquipmentTab
                  equipmentSearch={equipmentSearch}
                  setEquipmentSearch={setEquipmentSearch}
                  isEquipmentDialogOpen={isEquipmentDialogOpen}
                  setIsEquipmentDialogOpen={setIsEquipmentDialogOpen}
                  equipmentFormData={equipmentFormData}
                  setEquipmentFormData={setEquipmentFormData}
                  editingEquipment={editingEquipment}
                  importedFromLibrary={importedFromLibrary}
                  saveToLibrary={saveToLibrary}
                  setSaveToLibrary={setSaveToLibrary}
                  isAddingServiceType={isAddingServiceType}
                  setIsAddingServiceType={setIsAddingServiceType}
                  newServiceType={newServiceType}
                  setNewServiceType={setNewServiceType}
                  equipmentSiteFilter={equipmentSiteFilter}
                  setEquipmentSiteFilter={setEquipmentSiteFilter}
                  inlineNoteEquipId={inlineNoteEquipId}
                  setInlineNoteEquipId={setInlineNoteEquipId}
                  inlineNoteText={inlineNoteText}
                  setInlineNoteText={setInlineNoteText}
                  masterScopes={masterScopes}
                  libraryOptions={libraryOptions}
                  clientSites={clientSites}
                  serviceTypes={serviceTypes}
                  filteredEquipment={filteredEquipment}
                  uniqueEquipmentSites={uniqueEquipmentSites}
                  selectedSiteId={selectedSiteId}
                  handleEquipmentSubmit={handleEquipmentSubmit}
                  resetEquipmentForm={resetEquipmentForm}
                  handleLibrarySelect={handleLibrarySelect}
                  handleAddServiceType={handleAddServiceType}
                  handleEditEquipment={handleEditEquipment}
                  handleArchiveEquipment={handleArchiveEquipment}
                  handleInlineNoteAdd={handleInlineNoteAdd}
                  toggleEquipmentSort={toggleEquipmentSort}
                  getSiteNameById={getSiteNameById}
                  setActiveDetailTab={setActiveDetailTab}
                  workOrderTabRef={workOrderTabRef}
                />

                {/* Proposals tab extracted to
                    `pages/clients/tabs/ClientProposalsTab.jsx`
                    (iter180 component split). */}
                <ClientProposalsTab
                  filteredProposals={filteredProposals}
                  selectedClient={selectedClient}
                  selectedSiteId={selectedSiteId}
                  clientSites={clientSites}
                  proposalStatusOptions={proposalStatusOptions}
                  loadClientDetails={loadClientDetails}
                  setActiveDetailTab={setActiveDetailTab}
                />

                {/* Work Orders tab — extracted to
                    `pages/clients/tabs/ClientWorkOrdersTab.jsx`
                    (iter182 component split). */}
                <ClientWorkOrdersTab
                  workOrderTabRef={workOrderTabRef}
                  selectedClient={selectedClient}
                  clientSites={clientSites}
                  clientEquipment={clientEquipment}
                  getSiteNameById={getSiteNameById}
                  selectedSiteId={selectedSiteId}
                  pendingWorkOrderId={pendingWorkOrderId}
                  setPendingWorkOrderId={setPendingWorkOrderId}
                  pendingCopyWO={pendingCopyWO}
                  setPendingCopyWO={setPendingCopyWO}
                  pendingViewWO={pendingViewWO}
                  setPendingViewWO={setPendingViewWO}
                  woReturnTo={woReturnTo}
                  setWoReturnTo={setWoReturnTo}
                  formData={formData}
                />

                {/* Inspections tab extracted to
                    `pages/clients/tabs/ClientInspectionsTab.jsx`
                    (iter180 component split). */}
                <ClientInspectionsTab
                  clientInspections={clientInspections}
                  expandedInspId={expandedInspId}
                  setExpandedInspId={setExpandedInspId}
                  expandedInspData={expandedInspData}
                  setExpandedInspData={setExpandedInspData}
                />
                

                {/* History Tab */}
                <TabsContent value="history" className="mt-4 space-y-6">
                  {/* History tab extracted to `pages/clients/tabs/ClientHistoryTab.jsx`
                      (iter170 component split step #2). All five lists
                      (completed WOs, cancelled WOs, won props, lost
                      props, archived equipment) live there with their
                      own restore/copy/view row actions. */}
                  <ClientHistoryTab
                    completedWorkOrders={completedWorkOrders}
                    cancelledWorkOrders={cancelledWorkOrders}
                    wonProposals={wonProposals}
                    lostProposals={lostProposals}
                    filteredArchivedEquipment={filteredArchivedEquipment}
                    selectedClient={selectedClient}
                    selectedSiteId={selectedSiteId}
                    getSiteNameById={getSiteNameById}
                    getEquipmentTypeName={getEquipmentTypeName}
                    setPendingViewWO={setPendingViewWO}
                    setPendingCopyWO={setPendingCopyWO}
                    setActiveDetailTab={setActiveDetailTab}
                    loadClientDetails={loadClientDetails}
                    handleRestoreEquipment={handleRestoreEquipment}
                    communications={clientCommunications}
                    archivedNotes={formData.notes_history || []}
                    onRestoreNote={handleRestoreNote}
                    documentsSection={(
                      <ClientDocumentsTab
                        selectedClient={selectedClient}
                        clientDocuments={clientDocuments}
                        clientWorkOrders={clientWorkOrders}
                        docSearch={docSearch}
                        setDocSearch={setDocSearch}
                        docSort={docSort}
                        setDocSort={setDocSort}
                        setPendingViewWO={setPendingViewWO}
                        setActiveDetailTab={setActiveDetailTab}
                        setDocPreview={setDocPreview}
                      />
                    )}
                  />
                </TabsContent>
              </Tabs>

              {/* Add Site Dialog — extracted to
                  `pages/clients/dialogs/AddSiteDialog.jsx` (iter170
                  component split step #2). Owns no state; everything
                  comes from props so the dialog stays in sync with
                  the contact roster and tax-rate lookups. */}
              <AddSiteDialog
                open={isSiteDialogOpen}
                onOpenChange={setIsSiteDialogOpen}
                siteFormData={siteFormData}
                setSiteFormData={setSiteFormData}
                siteContactMode={siteContactMode}
                setSiteContactMode={setSiteContactMode}
                allCustomerContacts={allCustomerContacts}
                facilityTypes={facilityTypes}
                handleAddSite={handleAddSite}
              />

              {/* Delete Site Confirmation — extracted to
                  `pages/clients/dialogs/DeleteSiteConfirmDialog.jsx`. */}
              <DeleteSiteConfirmDialog
                open={deleteSiteConfirmOpen}
                onOpenChange={setDeleteSiteConfirmOpen}
                siteName={selectedSite?.name}
                onConfirm={handleDeleteSite}
              />

              <ArchiveCustomerSection
                archiveConfirmOpen={archiveConfirmOpen}
                setArchiveConfirmOpen={setArchiveConfirmOpen}
                selectedClient={selectedClient}
                handleArchive={handleArchive}
              />
              </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* In-app document preview modal (Documents tab → Eye button) */}
      {docPreview && (
        <DocumentPreviewModal
          title={docPreview.title}
          pdfSrc={docPreview.src}
          onClose={() => setDocPreview(null)}
          onDownload={docPreview.download}
          onEmail={docPreview.email}
        />
      )}

      {/* Quick Edit dialog — re-introduced Feb 2026 as an alternative
          path to the side-sheet. Opens via the pencil icon on each
          row. */}
      <EditClientDialog
        open={!!editDialogClient}
        onOpenChange={(o) => { if (!o) setEditDialogClient(null); }}
        client={editDialogClient}
        facilityTypes={facilityTypes}
        onSaved={loadClients}
      />
    </div>
  );
}


